import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, X, Merge, RefreshCcw, ChevronDown, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/errors";
import useDismissible from "@/lib/useDismissible";

const _str = (v) => (typeof v === "string" ? v : v?.it || v?.en || (v && Object.values(v)[0]) || "");

/**
 * Banner that appears above the dashboard whenever there are pending
 * sync alerts (ambiguous walk-in check-ins from the Guide app).
 *
 * Each alert offers two actions:
 *   - Merge → unisci il walk-in alla prenotazione candidata scelta
 *   - Mantieni separato → ignora l'allerta, walk-in resta a parte
 */
export default function SyncAlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  // iter190 — chiudi per 7 giorni via localStorage
  const [dismissed, dismiss] = useDismissible("sync-alerts", 7);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/guide/sync-alerts?status=pending");
      setAlerts(r.data?.items || []);
    } catch {
      setAlerts([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const resolve = async (alertId, action, targetCode = null) => {
    if (action === "delete") {
      if (!window.confirm("Eliminare definitivamente questa prenotazione walk-in? L'azione non può essere annullata.")) return;
    }
    setBusy(alertId);
    try {
      await api.post(`/admin/guide/sync-alerts/${alertId}/resolve`, {
        action,
        target_booking_code: targetCode,
      });
      const msg = action === "merge"
        ? "Walk-in unito alla prenotazione"
        : action === "delete"
        ? "Walk-in eliminato"
        : "Allerta archiviata";
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setBusy(null);
    }
  };

  if (alerts.length === 0) return null;
  if (dismissed) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl overflow-hidden relative" data-testid="sync-alerts-banner">
      {/* iter190 — X per chiudere il banner per 7 giorni */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-amber-200 text-amber-800/70 hover:text-amber-900 transition-colors z-10"
        aria-label="Chiudi per 7 giorni"
        title="Nascondi per 7 giorni"
        data-testid="sync-alerts-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-100/50 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-amber-900 flex items-center gap-2">
              Sincronizzazioni da verificare
              <span className="bg-amber-600 text-white text-xs font-bold rounded-full px-2 py-0.5">{alerts.length}</span>
            </div>
            <p className="text-xs text-amber-800 mt-0.5">
              Check-in arrivati dalla Guida con dati simili ad altre prenotazioni. Verifica se vanno unite o tenute separate.
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-amber-700 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-3">
          {alerts.map((a) => {
            const incoming = a.incoming_payload || {};
            const incomingGuest = incoming.guest || incoming.data || {};
            const incomingName = [incomingGuest.first_name, incomingGuest.last_name].filter(Boolean).join(" ") || "?";
            const arrival = incoming.arrival_date || incoming.checked_in_at?.slice(0, 10) || "?";
            return (
              <div key={a.id} className="bg-white border border-amber-200 rounded-lg p-3" data-testid={`sync-alert-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-semibold text-amber-900">🚶 {incomingName}</span>
                  <span className="text-xs text-amber-700">arrivato {arrival}</span>
                  <span className="text-[10px] font-mono bg-amber-100 px-1.5 py-0.5 rounded text-amber-800">walk-in: {a.new_walkin_code}</span>
                </div>
                <p className="text-xs text-[#635F59] mb-2">Possibili match esistenti — scegli se unire:</p>
                <div className="space-y-1.5">
                  {(a.candidates || []).map((c) => {
                    const cg = c.guest || {};
                    const cname = [cg.first_name, cg.last_name].filter(Boolean).join(" ") || "?";
                    return (
                      <div key={c.code} className="flex items-center gap-2 bg-[#FDFBF7] rounded px-2 py-1.5 text-xs">
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-[#1E3A5F]">{cname}</span>
                          <span className="text-[#635F59] ml-2">{_str(c.room_name)}</span>
                          <span className="text-[#635F59] ml-2">{c.check_in} → {c.check_out}</span>
                          <span className="text-[10px] font-mono ml-2 text-[#635F59]">{c.code}</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => resolve(a.id, "merge", c.code)}
                          disabled={busy === a.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5 py-1 h-auto"
                          data-testid={`sync-alert-merge-${a.id}-${c.code}`}
                        >
                          <Merge className="w-3 h-3 mr-1" /> Unisci qui
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap justify-end gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => resolve(a.id, "delete")}
                    disabled={busy === a.id}
                    className="text-xs text-rose-700 hover:text-rose-900 hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid={`sync-alert-delete-${a.id}`}
                    title="Elimina definitivamente questo walk-in (non lo voglio né unire né tenere)"
                  >
                    <Trash2 className="w-3 h-3" /> Elimina walk-in
                  </button>
                  <button
                    type="button"
                    onClick={() => resolve(a.id, "keep_separate")}
                    disabled={busy === a.id}
                    className="text-xs text-[#635F59] hover:text-[#2D2926] flex items-center gap-1 disabled:opacity-50"
                    data-testid={`sync-alert-dismiss-${a.id}`}
                  >
                    <X className="w-3 h-3" /> Tieni separato (è un'altra persona)
                  </button>
                </div>
              </div>
            );
          })}
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="text-xs w-full"
          >
            <RefreshCcw className="w-3 h-3 mr-1" /> Aggiorna
          </Button>
        </div>
      )}
    </div>
  );
}
